import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Code,
  Container,
  CopyButton,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconCopy, IconKey } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { joinPaths } from '~lib';
import { breakAll } from '~portal/components/common/common.css';
import { getAppEnv } from '~portal/env';

export const Route = createFileRoute('/claim/$token')({
  component: ClaimPage,
});

// -- Types --

type ClaimSuccessResponse = {
  apiKey: string;
  keyPrefix: string;
  orgId: string;
};

type ClaimState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: ClaimSuccessResponse }
  | { status: 'error'; message: string };

// -- Component --

function ClaimPage() {
  const { token } = Route.useParams();
  const [state, setState] = useState<ClaimState>({ status: 'idle' });

  const handleClaim = async () => {
    setState({ status: 'loading' });

    try {
      const env = getAppEnv();
      const res = await fetch(joinPaths(env.backendApiUrl, '/v1/invites/claim'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      if (!res.ok) {
        if (res.status === 404) {
          setState({
            status: 'error',
            message: 'This invite link has expired or has already been used.',
          });
          return;
        }

        const body = await res.text();
        setState({
          status: 'error',
          message: body || `Request failed with status ${res.status}`,
        });
        return;
      }

      const data: ClaimSuccessResponse = await res.json();
      setState({ status: 'success', data });
    } catch {
      setState({
        status: 'error',
        message: 'Unable to reach the server. Please check your connection and try again.',
      });
    }
  };

  return (
    <Container size="sm" py="xl" mih="100vh">
      <Stack align="center" justify="center" mih="80vh" gap="lg">
        <Paper withBorder p="xl" w="100%">
          <Stack gap="lg" align="center">
            {/* Header */}
            <IconKey size={48} stroke={1.5} />
            <Title order={2} ta="center">
              H2GC Data API
            </Title>

            {/* Idle state */}
            {state.status === 'idle' && (
              <Stack gap="md" align="center" w="100%">
                <Text ta="center" c="dimmed">
                  You've been invited to access the H2GC Data API.
                </Text>
                <Button
                  size="lg"
                  leftSection={<IconKey size={20} />}
                  onClick={handleClaim}
                  fullWidth
                  maw={320}
                >
                  Claim Your API Key
                </Button>
                <Text size="xs" c="dimmed" ta="center">
                  This invite can only be claimed once.
                </Text>
              </Stack>
            )}

            {/* Loading state */}
            {state.status === 'loading' && (
              <Button size="lg" loading fullWidth maw={320} disabled>
                Claiming...
              </Button>
            )}

            {/* Success state */}
            {state.status === 'success' && (
              <Stack gap="md" w="100%">
                <Alert color="green" title="API Key Generated Successfully">
                  Your API key has been created and is ready to use.
                </Alert>

                <Alert color="red" variant="filled">
                  <Text fw={600}>
                    This key is shown only once. Copy it now and store it securely.
                  </Text>
                </Alert>

                <div>
                  <Text size="sm" fw={500} mb={4}>
                    API Key
                  </Text>
                  <Group gap="xs" wrap="nowrap">
                    <Code block flex={1} className={breakAll}>
                      {state.data.apiKey}
                    </Code>
                    <CopyButton value={state.data.apiKey}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? 'Copied' : 'Copy API key'}>
                          <Button
                            variant="subtle"
                            size="compact-sm"
                            onClick={copy}
                            color={copied ? 'teal' : 'gray'}
                          >
                            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                          </Button>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                </div>

                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    Organisation:
                  </Text>
                  <Badge variant="light">{state.data.orgId}</Badge>
                </Group>

                <Paper withBorder p="sm" bg="dark.8" radius="sm">
                  <Text size="xs" c="dimmed">
                    Use this key in the <Code>x-api-key</Code> header when calling the API.
                  </Text>
                </Paper>
              </Stack>
            )}

            {/* Error state */}
            {state.status === 'error' && (
              <Stack gap="md" w="100%">
                <Alert color="red" title="Unable to Claim API Key">
                  {state.message}
                </Alert>
                <Text size="sm" c="dimmed" ta="center">
                  Please contact your administrator to request a new invite.
                </Text>
              </Stack>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
